// ──────────────────────────────────────────────────────────────────────────
// DiarioStatisticheSintesi.js — Sotto-tab "📊 Sintesi" della tab Statistiche
// del Diario Operativo. Implementazione v130 (TASK 8 Fase 5).
// Riferimenti: design v129 (D91-D103) + edge case v130 (D106-D116).
//
// Refactor v131: estratti in src/components/stats/ i componenti riusabili
// (statsFormat, SpiegaPopover, KpiCard, MiniToggle, BannerContesto) per
// uso condiviso con i sotto-tab "Per sistema", "Matrici P/L", "Confronto".
// Logica di Sintesi invariata. Restano locali ToggleVistaEquity ed EquityLine
// (specifici di questo sotto-tab).
// ──────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { fmtEur, fmtPct, fmtNum, fmtData } from "../components/stats/statsFormat";
import SpiegaPopover from "../components/stats/SpiegaPopover";
import KpiCard from "../components/stats/KpiCard";
import MiniToggle from "../components/stats/MiniToggle";
import BannerContesto from "../components/stats/BannerContesto";
import { useStatisticheFiltri } from "../contexts/StatisticheFiltriContext";

import { API } from "../config";

// ── Utility, popover e card riusabili: estratti in src/components/stats/ ──
// (statsFormat.js, SpiegaPopover.js, KpiCard.js, MiniToggle.js,
//  BannerContesto.js) — refactor v131, logica invariata.

// ── Toggle principale equity (Per data / Per operazione) ──────────────────

function ToggleVistaEquity({ vista, onChange }) {
  return (
    <span style={{
      display: "inline-flex", background: "#eef2f7",
      borderRadius: 8, padding: 3,
    }}>
      {[
        { id: "data", label: "Per data" },
        { id: "op",   label: "Per operazione" },
      ].map((v) => (
        <button
          key={v.id}
          type="button"
          onClick={() => onChange(v.id)}
          style={{
            background: vista === v.id ? "#fff" : "transparent",
            color: vista === v.id ? "#1f2a37" : "#6b7280",
            border: vista === v.id ? "1px solid #d6dde6" : "1px solid transparent",
            borderRadius: 6, padding: "5px 12px",
            fontSize: 12, fontWeight: vista === v.id ? 600 : 400,
            cursor: "pointer",
          }}
        >{v.label}</button>
      ))}
    </span>
  );
}

// ── Equity line SVG (no librerie esterne) ─────────────────────────────────
// Disegna la linea + baseline tratteggiata + marker "Oggi" sull'ultimo punto.
// Tooltip al passaggio del mouse.

function EquityLine({ vista, dataPerData, dataPerOp, capInizio, dataInizioStagione, stagioneInCorso }) {
  const W = 760, H = 240, padL = 50, padR = 14, padT = 10, padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const [hover, setHover] = useState(null);

  // Costruisco la serie da disegnare in base alla vista
  let serie = [];
  if (vista === "data") {
    // Espando i giorni vuoti tra inizio stagione e ultimo giorno operativo
    if (dataPerData && dataPerData.length > 0 && dataInizioStagione) {
      const ultimaOp = dataPerData[dataPerData.length - 1].data;
      const oggi = new Date().toISOString().slice(0, 10);
      const fine = stagioneInCorso ? oggi : ultimaOp;
      let cap = capInizio;
      const start = new Date(dataInizioStagione);
      const end = new Date(fine);
      const map = new Map(dataPerData.map(d => [d.data, d]));
      const cur = new Date(start);
      while (cur <= end) {
        const k = cur.toISOString().slice(0, 10);
        if (map.has(k)) cap = map.get(k).equity;
        serie.push({ label: k, valore: cap, dataIso: k, isOp: map.has(k), nOp: map.has(k) ? map.get(k).n_op : 0, plGiorno: map.has(k) ? map.get(k).pl_giorno : 0 });
        cur.setDate(cur.getDate() + 1);
      }
    } else if (dataInizioStagione) {
      // Stagione vuota: linea piatta a capInizio dal data_inizio a oggi
      const oggi = new Date().toISOString().slice(0, 10);
      serie = [
        { label: dataInizioStagione, valore: capInizio, dataIso: dataInizioStagione, isOp: false },
        { label: oggi, valore: capInizio, dataIso: oggi, isOp: false },
      ];
    }
  } else {
    // Per operazione
    if (dataPerOp && dataPerOp.length > 0) {
      // Punto 0 = capitale di inizio
      serie.push({ label: "0", valore: capInizio, dataIso: dataInizioStagione, isOp: false, n: 0 });
      dataPerOp.forEach(o => {
        serie.push({ label: String(o.n), valore: o.equity, dataIso: o.data, isOp: true, n: o.n, pl: o.pl });
      });
    } else if (capInizio) {
      serie = [{ label: "0", valore: capInizio, dataIso: dataInizioStagione, isOp: false }];
    }
  }

  if (serie.length === 0) {
    return (
      <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13 }}>
        Nessun dato da visualizzare per il grafico.
      </div>
    );
  }

  // Calcolo bounds Y
  const valori = serie.map(p => p.valore);
  let yMin = Math.min(...valori, capInizio);
  let yMax = Math.max(...valori, capInizio);
  const range = yMax - yMin || 1;
  const padding = range * 0.1 || capInizio * 0.005;
  yMin -= padding;
  yMax += padding;

  const xAt = (i) => padL + (serie.length === 1 ? innerW / 2 : (i / (serie.length - 1)) * innerW);
  const yAt = (v) => padT + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  // Path della linea principale
  const linePath = serie.map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(p.valore).toFixed(1)}`).join(" ");
  // Path area (sotto la linea, fino al baseline = capInizio)
  const yBase = yAt(capInizio);
  const areaPath = serie.length > 1
    ? `M ${xAt(0).toFixed(1)} ${yBase} L ${serie.map((p, i) => `${xAt(i).toFixed(1)} ${yAt(p.valore).toFixed(1)}`).join(" L ")} L ${xAt(serie.length - 1).toFixed(1)} ${yBase} Z`
    : "";

  // Tick X (max 8 etichette equispaziate)
  const nTick = Math.min(serie.length, 8);
  const tickIdx = nTick === 1 ? [0] : Array.from({ length: nTick }, (_, i) => Math.round(i / (nTick - 1) * (serie.length - 1)));
  // Tick Y (5 linee orizzontali)
  const yTickN = 5;
  const yTicks = Array.from({ length: yTickN }, (_, i) => yMin + (i / (yTickN - 1)) * (yMax - yMin));

  // Ultimo punto = "oggi" se stagione in corso E vista per data, oppure ultima op
  const idxUltimo = serie.length - 1;

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, display: "block" }}
           onMouseLeave={() => setHover(null)}>
        {/* Tick Y + griglia */}
        {yTicks.map((v, i) => (
          <g key={`yt-${i}`}>
            <line x1={padL} x2={W - padR} y1={yAt(v)} y2={yAt(v)}
                  stroke="#eef0f3" strokeWidth="1" />
            <text x={padL - 6} y={yAt(v) + 3} fontSize="10" fill="#9ca3af" textAnchor="end">
              {Math.round(v).toLocaleString("it-IT")} €
            </text>
          </g>
        ))}
        {/* Baseline capitale di inizio stagione (tratteggiata) */}
        <line x1={padL} x2={W - padR} y1={yBase} y2={yBase}
              stroke="#9ca3af" strokeDasharray="4 4" strokeWidth="1" />
        {/* Area sotto la linea */}
        {areaPath && <path d={areaPath} fill="rgba(24, 95, 165, 0.08)" />}
        {/* Linea principale */}
        <path d={linePath} stroke="#185FA5" strokeWidth="1.8" fill="none" />
        {/* Marker ultimo punto (sempre, "Oggi" se stagione in corso) */}
        {serie.length > 0 && (
          <circle cx={xAt(idxUltimo)} cy={yAt(serie[idxUltimo].valore)} r="4.5"
                  fill="#185FA5" stroke="#fff" strokeWidth="1.5" />
        )}
        {/* Tick X */}
        {tickIdx.map(i => {
          const p = serie[i];
          let label;
          if (vista === "data") {
            label = fmtData(p.dataIso);
          } else {
            label = p.label === "0" ? "0" : `Op. ${p.label}`;
          }
          return (
            <text key={`xt-${i}`} x={xAt(i)} y={H - padB + 16} fontSize="10"
                  fill="#9ca3af" textAnchor="middle">{label}</text>
          );
        })}
        {/* Hover overlay invisibile */}
        {serie.map((p, i) => (
          <rect key={`h-${i}`}
                x={xAt(i) - (innerW / serie.length / 2)} y={padT}
                width={innerW / serie.length} height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover({ idx: i, p })} />
        ))}
        {/* Tooltip */}
        {hover && (
          <g>
            <line x1={xAt(hover.idx)} x2={xAt(hover.idx)} y1={padT} y2={padT + innerH}
                  stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={xAt(hover.idx)} cy={yAt(hover.p.valore)} r="3.5"
                    fill="#185FA5" stroke="#fff" strokeWidth="1.5" />
          </g>
        )}
      </svg>
      {/* Tooltip in HTML (più leggibile) */}
      {hover && (
        <div style={{
          marginTop: -28, marginLeft: 8,
          fontSize: 11, color: "#4a5568",
          background: "#fff", padding: "4px 10px",
          border: "1px solid #d6dde6", borderRadius: 6,
          display: "inline-block",
        }}>
          {vista === "data" ? (
            <>
              <strong>{fmtData(hover.p.dataIso)}</strong>
              {hover.p.isOp ? (
                <> · {hover.p.nOp} op. · P/L giornata: <strong>{fmtEur(hover.p.plGiorno)}</strong> · Capitale: <strong>{fmtEur(hover.p.valore)}</strong></>
              ) : (
                <> · Nessuna operazione · Capitale: <strong>{fmtEur(hover.p.valore)}</strong></>
              )}
            </>
          ) : (
            <>
              {hover.p.label === "0" ? <strong>Inizio stagione</strong> : <strong>Op. {hover.p.label}</strong>}
              {" · "}{fmtData(hover.p.dataIso)}
              {hover.p.label !== "0" && hover.p.pl !== undefined && (
                <> · P/L: <strong style={{ color: hover.p.pl > 0 ? "#0d8a4e" : (hover.p.pl < 0 ? "#c53030" : "#1f2a37") }}>{fmtEur(hover.p.pl)}</strong></>
              )}
              {" · Capitale: "}<strong>{fmtEur(hover.p.valore)}</strong>
            </>
          )}
        </div>
      )}
      {/* Legenda */}
      <div style={{ display: "flex", gap: 18, marginTop: 6, fontSize: 11, color: "#6b7280", flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 2, background: "#185FA5" }}></span>Capitale realizzato
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 0, borderTop: "1px dashed #9ca3af" }}></span>
          Capitale di inizio stagione ({fmtEur(capInizio, 0)})
        </span>
        {stagioneInCorso && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, background: "#185FA5", borderRadius: "50%", border: "1.5px solid #fff", outline: "1px solid #185FA5" }}></span>
            Oggi
          </span>
        )}
      </div>
    </div>
  );
}

// ── Banner contestuale per stato vuoto / filtro a 0 risultati ─────────────

// ── Componente principale ─────────────────────────────────────────────────

export default function DiarioStatisticheSintesi() {
  // v131 sub-deploy 3: leggo i filtri condivisi dal Context (Stagione, Periodo, Sistema).
  // Quando l'utente cambia un filtro, queryParams cambia → carica() si rilancia
  // automaticamente e la Sintesi si ricalcola sui dati filtrati.
  const filtri = useStatisticheFiltri();

  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);

  // Toggle vista equity (persistenti in sessionStorage — Punto 3 design-first)
  const [vistaEquity, setVistaEquity] = useState(() => {
    return sessionStorage.getItem("statistiche_vista_equity") || "data";
  });
  useEffect(() => {
    sessionStorage.setItem("statistiche_vista_equity", vistaEquity);
  }, [vistaEquity]);

  // Toggle drawdown vs picco / vs inizio stagione
  const [modoDrawdown, setModoDrawdown] = useState(() => {
    return sessionStorage.getItem("statistiche_modo_drawdown") || "picco";
  });
  useEffect(() => {
    sessionStorage.setItem("statistiche_modo_drawdown", modoDrawdown);
  }, [modoDrawdown]);

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filtri.queryParams).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") params.set(k, v);
      });
      const qs = params.toString();
      const url = `${API}/diario/statistiche/sintesi` + (qs ? `?${qs}` : "");
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDati(d);
    } catch (e) {
      setErrore(e.message || "Errore di rete");
    } finally {
      setLoading(false);
    }
  }, [filtri.queryParams]);

  useEffect(() => {
    carica();
  }, [carica]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Caricamento statistiche…</div>;
  }
  if (errore) {
    return (
      <div style={{ padding: 24, color: "#9b2c2c", background: "#fed7d7", borderRadius: 8 }}>
        Errore: {errore}
      </div>
    );
  }
  if (!dati) {
    return null;
  }

  const stagioneVuota = !dati.stagione;
  const senzaOperazioni = dati.conteggi.totali === 0;

  // Banner contestuale (Edge case 1, 4)
  let banner = null;
  if (stagioneVuota) {
    banner = "Nessuna stagione configurata. Vai in ⚙️ Impostazioni → Stagioni per crearne una.";
  } else if (senzaOperazioni) {
    banner = "Nessuna operazione registrata in questa stagione. Inizia a registrarle nella tab 📓 Diario.";
  }

  // Helper colori
  const colorePL = dati.pl.netto_eur > 0 ? "#0d8a4e" : (dati.pl.netto_eur < 0 ? "#c53030" : "#1f2a37");

  const drawdownEur = dati.drawdown.max_eur;
  const drawdownPct = modoDrawdown === "picco"
    ? dati.drawdown.max_pct_vs_picco
    : dati.drawdown.max_pct_vs_inizio;

  return (
    <div style={{ padding: "16px 4px" }}>
      {/* Header sotto-tab */}
      {dati.stagione && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          marginBottom: 12, flexWrap: "wrap", gap: 8,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#1f2a37" }}>
              Sintesi · Stagione {dati.stagione.etichetta}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Dal {fmtData(dati.stagione.data_inizio)}
              {dati.stagione.data_fine ? ` al ${fmtData(dati.stagione.data_fine)}` : " · in corso"}
              {" · capitale di inizio "}
              <strong>{fmtEur(dati.stagione.capitale_inizio_stagione_eur, 0)}</strong>
            </div>
          </div>
        </div>
      )}

      {banner && (
        <BannerContesto messaggio={banner} />
      )}

      {/* KPI Cards: 4 principali */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
        gap: 12, marginBottom: 12,
      }}>
        <KpiCard
          label="P/L stagione"
          valore={fmtEur(dati.pl.netto_eur)}
          colore={colorePL}
          sottotesto={
            <>
              <span>{fmtPct(dati.pl.pct_vs_inizio_stagione)} su {fmtEur(dati.stagione?.capitale_inizio_stagione_eur || 0, 0)}</span>
              {dati.conteggi.open > 0 && (
                <div style={{ marginTop: 2 }}>
                  + {dati.conteggi.open} in attesa ({fmtEur(dati.conteggi.size_open_eur, 0)} impegnati)
                </div>
              )}
            </>
          }
          spiega={{
            titolo: "P/L stagione",
            formula: "Somma di tutti i profitti/perdite netti delle operazioni chiuse della stagione.",
            dettagli: `${dati.conteggi.chiuse} operazioni chiuse · P/L lordo ${fmtEur(dati.pl.lordo_eur)} − fee ${fmtEur(dati.pl.fee_totali_eur)} = P/L netto ${fmtEur(dati.pl.netto_eur)}. La percentuale è calcolata sul capitale di inizio stagione (${fmtEur(dati.stagione?.capitale_inizio_stagione_eur || 0, 0)}).`,
          }}
        />

        <KpiCard
          label="Operazioni"
          valore={fmtNum(dati.conteggi.totali)}
          sottotesto={
            <>
              <div>
                {dati.conteggi.chiuse} chiuse
                {dati.conteggi.open > 0 && ` · ${dati.conteggi.open} in attesa`}
              </div>
              {dati.conteggi.fuori_sistema > 0 && (
                <div style={{ marginTop: 2 }}>
                  di cui {dati.conteggi.fuori_sistema} fuori sistema
                </div>
              )}
            </>
          }
          spiega={{
            titolo: "Operazioni",
            formula: "Totale delle operazioni della stagione (chiuse + in attesa).",
            dettagli: `Vinte: ${dati.conteggi.vinte} · Perse: ${dati.conteggi.perse} · Void: ${dati.conteggi.void} · Open: ${dati.conteggi.open}. ${dati.conteggi.fuori_sistema > 0 ? `${dati.conteggi.fuori_sistema} operazioni sono "fuori sistema" (registrate manualmente senza un sistema associato).` : ""}`,
          }}
        />

        <KpiCard
          label="Drawdown massimo"
          valore={fmtPct(-Math.abs(drawdownPct))}
          colore={drawdownEur > 0 ? "#c53030" : "#1f2a37"}
          sottotesto={
            drawdownEur > 0
              ? <>{fmtEur(-Math.abs(drawdownEur))} dal picco</>
              : "Nessuna discesa registrata"
          }
          toggleNode={
            <MiniToggle
              valori={[
                { id: "picco", label: "vs picco" },
                { id: "inizio", label: "vs inizio" },
              ]}
              attivo={modoDrawdown}
              onChange={setModoDrawdown}
            />
          }
          spiega={{
            titolo: "Drawdown massimo",
            formula: modoDrawdown === "picco"
              ? "Massima discesa dal picco precedente: (picco − valle) / picco. Standard finanziario."
              : "Massima distanza dal capitale di inizio stagione: (inizio − valle) / inizio.",
            dettagli: drawdownEur > 0
              ? `Dal massimo ${fmtData(dati.drawdown.max_data_picco)} alla valle ${fmtData(dati.drawdown.max_data_valle)}. Differenza: ${fmtEur(drawdownEur)}. Calcolato sulla singola stagione.`
              : "La stagione non ha ancora registrato discese significative dal picco massimo.",
          }}
        />

        <KpiCard
          label="Win rate"
          valore={dati.kpi_statistiche.sotto_soglia ? "—" : fmtPct(dati.kpi_statistiche.win_rate_pct, 1)}
          sottotesto={
            dati.kpi_statistiche.sotto_soglia
              ? `Servono almeno ${dati.kpi_statistiche.soglia_minima} operazioni`
              : `${dati.conteggi.vinte} vinte / ${dati.conteggi.chiuse - dati.conteggi.void}`
          }
          spiega={{
            titolo: "Win rate",
            formula: "Vinte / (Vinte + Perse). Le operazioni Void sono escluse dal calcolo.",
            dettagli: dati.kpi_statistiche.sotto_soglia
              ? `Calcolo non disponibile: meno di ${dati.kpi_statistiche.soglia_minima} operazioni chiuse.`
              : `Punta: ${fmtPct(dati.kpi_statistiche.win_rate_punta_pct, 1)} · Banca: ${fmtPct(dati.kpi_statistiche.win_rate_banca_pct, 1)}`,
          }}
        />
      </div>

      {/* KPI Cards: 4 secondarie */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
        gap: 12, marginBottom: 20,
      }}>
        <KpiCard
          label="PPE (profitto per evento)"
          valore={dati.kpi_statistiche.sotto_soglia ? "—" : fmtEur(dati.kpi_statistiche.ppe_eur)}
          sottotesto={
            dati.kpi_statistiche.sotto_soglia
              ? `Servono almeno ${dati.kpi_statistiche.soglia_minima} operazioni`
              : "P/L medio per operazione chiusa"
          }
          spiega={{
            titolo: "PPE — Profitto per evento",
            formula: "P/L netto totale / numero di operazioni chiuse.",
            dettagli: dati.kpi_statistiche.sotto_soglia
              ? "Calcolo non disponibile."
              : `${fmtEur(dati.pl.netto_eur)} / ${dati.conteggi.chiuse} = ${fmtEur(dati.kpi_statistiche.ppe_eur)}`,
          }}
        />

        <KpiCard
          label="Quota media"
          valore={dati.kpi_statistiche.sotto_soglia ? "—" : fmtNum(dati.kpi_statistiche.quota_media, 2)}
          sottotesto={
            dati.kpi_statistiche.sotto_soglia
              ? `Servono almeno ${dati.kpi_statistiche.soglia_minima} operazioni`
              : "Quota effettiva media delle operazioni chiuse"
          }
          spiega={{
            titolo: "Quota media effettiva",
            formula: "Media aritmetica delle quote effettive di tutte le operazioni chiuse.",
            dettagli: dati.kpi_statistiche.sotto_soglia
              ? "Calcolo non disponibile."
              : `Calcolata su ${dati.conteggi.chiuse} operazioni chiuse.`,
          }}
        />

        <KpiCard
          label="Slippage quote medio"
          valore={dati.kpi_statistiche.sotto_soglia
            ? "—"
            : (dati.kpi_statistiche.slippage_medio_eur === null || dati.kpi_statistiche.slippage_medio_eur === undefined
                ? "—"
                : fmtEur(dati.kpi_statistiche.slippage_medio_eur))}
          sottotesto={
            dati.kpi_statistiche.sotto_soglia
              ? `Servono almeno ${dati.kpi_statistiche.soglia_minima} operazioni`
              : (dati.kpi_statistiche.slippage_n_op_escluse > 0
                  ? `Calcolato su ${dati.kpi_statistiche.slippage_n_op_calcolo} di ${dati.conteggi.chiuse} operazioni chiuse`
                  : `Calcolato su ${dati.kpi_statistiche.slippage_n_op_calcolo} operazioni`)
          }
          spiega={{
            titolo: "Slippage quote medio",
            formula: "È il vero costo (o guadagno) medio in euro per operazione che il book ti ha generato rispetto alle quote del segnale del sistema. Negativo: il book ti è andato contro. Positivo: ti ha favorito.",
            dettagli: `Come si calcola: per ogni operazione chiusa, confronto il P/L netto che avresti ottenuto con la quota teorica del sistema con quello effettivo della quota di book; faccio la media sulle operazioni della stagione. Esempio: una Punta vincente con stake 10 €, quota teorica 2,10, quota effettiva 2,05 → ti è costata 0,49 € (al netto delle fee). Su una Punta persa lo slippage in euro è 0 — la quota non incide sulla perdita. Calcolato su ${dati.kpi_statistiche.slippage_n_op_calcolo} di ${dati.conteggi.chiuse} operazioni chiuse (le altre non hanno entrambe le quote registrate).`,
          }}
        />

        <KpiCard
          label="Slippage quote cumulativo"
          valore={dati.kpi_statistiche.sotto_soglia
            ? "—"
            : (dati.kpi_statistiche.slippage_cumulativo_eur === null || dati.kpi_statistiche.slippage_cumulativo_eur === undefined
                ? "—"
                : fmtEur(dati.kpi_statistiche.slippage_cumulativo_eur))}
          sottotesto={
            dati.kpi_statistiche.sotto_soglia
              ? `Servono almeno ${dati.kpi_statistiche.soglia_minima} operazioni`
              : "Vero costo (o guadagno) in euro generato dal book"
          }
          spiega={{
            titolo: "Slippage quote cumulativo",
            formula: "È il vero costo (o guadagno) in euro che il book ti ha generato rispetto alle quote del segnale del sistema. Negativo: il book ti è andato contro. Positivo: ti ha favorito.",
            dettagli: `Come si calcola: per ogni operazione chiusa, confronto il P/L netto che avresti ottenuto con la quota teorica del sistema con quello effettivo della quota di book; sommo le differenze sulle operazioni della stagione. Esempio: una Punta vincente con stake 10 €, quota teorica 2,10, quota effettiva 2,05 → ti è costata 0,49 € (al netto delle fee). Su una Punta persa lo slippage in euro è 0 — la quota non incide sulla perdita. Calcolato su ${dati.kpi_statistiche.slippage_n_op_calcolo} di ${dati.conteggi.chiuse} operazioni chiuse (le altre non hanno entrambe le quote registrate).`,
          }}
        />
      </div>

      {/* Equity line con toggle vista */}
      {!senzaOperazioni && (
        <div style={{
          background: "#fff", border: "1px solid #e5e7eb",
          borderRadius: 12, padding: 18,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 12, flexWrap: "wrap", gap: 10,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1f2a37" }}>Equity line</div>
            <ToggleVistaEquity vista={vistaEquity} onChange={setVistaEquity} />
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
            {vistaEquity === "data"
              ? "Asse X = giorni dalla partenza stagione. Linea piatta = pausa operativa."
              : "Asse X = numero progressivo operazioni. Ogni punto è un esito chiuso."}
          </div>
          <EquityLine
            vista={vistaEquity}
            dataPerData={dati.equity.per_data}
            dataPerOp={dati.equity.per_op}
            capInizio={dati.equity.capitale_inizio_stagione_eur}
            dataInizioStagione={dati.stagione?.data_inizio}
            stagioneInCorso={dati.stagione?.in_corso}
          />
        </div>
      )}
    </div>
  );
}
